import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Charset, Document, Encoder } from 'flexsearch';
import { MessageModel } from '../types/models';
import { splitAssistantResponse } from '../utils/assistantResponseParser';

const SEARCH_ID_PREFIX = {
    CHAT: 'chat_',
    FILE: 'file_',
    MESSAGE: 'msg_',
} as const;

const CHAT_SEARCH_CONFIG = {
    TOKENIZE: 'full' as const,
    RESOLUTION: 9,
    CACHE: 100,
    SUGGEST_MIN_LENGTH: 3,
    DEBOUNCE_MS: 150,
} as const;

const CHAT_SEARCH_DOCUMENT_SCHEMA = {
    id: 'id' as const,
    index: ['content'],
    store: ['messageIndex', 'content'],
};

const createChatSearchEncoder = () =>
    new Encoder({
        normalize: true,
        dedupe: true,
        include: {
            letter: true,
            number: true,
            symbol: true,
            punctuation: true,
            control: true,
        },
    }).assign(Charset.LatinExtra);

const createChatSearchIndexConfig = () => ({
    document: CHAT_SEARCH_DOCUMENT_SCHEMA,
    tokenize: CHAT_SEARCH_CONFIG.TOKENIZE,
    resolution: CHAT_SEARCH_CONFIG.RESOLUTION,
    cache: CHAT_SEARCH_CONFIG.CACHE,
    encoder: createChatSearchEncoder(),
});

/**
 * useChatSearch - Custom hook for in-chat text search with FlexSearch
 * 
 * Provides search functionality within chat messages:
 * - Fast indexed search with FlexSearch
 * - Case-insensitive text matching
 * - Enabled suggestions for fuzzy matching
 * - Tolerant match highlighting
 * - Navigation between matches
 * - Auto-scroll to current match
 * - Message highlighting
 * - Content preprocessing to handle escape sequences
 */

export type RoleFilter = 'all' | 'user' | 'assistant';

interface SearchMatch {
    messageIndex: number;
    matchIndex: number;
    startOffset: number;
    endOffset: number;
}

interface ChatSearchDocument {
    id: string;
    messageIndex: number;
    content: string;
    [key: string]: any; // Index signature for FlexSearch compatibility
}

/**
 * Preprocess content for indexing and searching
 * Removes escape sequences like \" and normalizes whitespace
 */
const preprocessContent = (content: string): string => {
    if (!content) return '';
    
    // Handle escape sequences in correct order
    let processed = content
        .replace(/\\\\/g, '\u0000')  // Temporarily protect literal backslashes
        .replace(/\\"/g, '"')         // \" -> "
        .replace(/\\'/g, "'")         // \' -> '
        .replace(/\\n/g, ' ')         // \n -> space
        .replace(/\\r/g, ' ')         // \r -> space
        .replace(/\\t/g, ' ')         // \t -> space
        .split('\u0000').join('\\');   // Restore literal backslashes
    
    // Normalize multiple spaces to single space
    processed = processed.replace(/\s+/g, ' ');
    
    return processed.trim();
};

/**
 * Extract the searchable content from a message.
 * For assistant messages, returns only the final response (excludes analysis/reasoning).
 */
const getSearchableContent = (message: MessageModel): string => {
    if (!message.content) return '';
    if (message.role === 'assistant') {
        const { final } = splitAssistantResponse(message.content, message.model);
        return final || message.content;
    }
    return message.content;
};

export const useChatSearch = (
    chatHistory: MessageModel[],
    chatContainerRef: React.RefObject<HTMLDivElement | null>,
    isGenerating?: boolean
) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
    const [matches, setMatches] = useState<SearchMatch[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const flexSearchIndex = useRef<Document<ChatSearchDocument> | null>(null);
    const latestQueryRef = useRef(searchQuery);
    const latestRoleFilterRef = useRef(roleFilter);
    const lastIndexedHistoryLengthRef = useRef(0);
    const wasGeneratingRef = useRef(false);

    useEffect(() => {
        latestQueryRef.current = searchQuery;
    }, [searchQuery]);

    useEffect(() => {
        latestRoleFilterRef.current = roleFilter;
    }, [roleFilter]);

    /**
     * Initialize or rebuild FlexSearch index when chat history changes
     * Only re-index when not actively streaming to avoid repeated indexing per token
     */
    useEffect(() => {
        // Skip re-indexing while streaming is in progress
        if (isGenerating) {
            wasGeneratingRef.current = true;
            return;
        }

        // Only re-index if:
        // 1. History length changed (new messages added)
        // 2. We just finished generating (streaming completed)
        const historyLengthChanged = chatHistory.length !== lastIndexedHistoryLengthRef.current;
        const justFinishedGenerating = wasGeneratingRef.current && !isGenerating;

        if (!historyLengthChanged && !justFinishedGenerating) {
            return;
        }

        // Create FlexSearch index
        flexSearchIndex.current = new Document<ChatSearchDocument>(
            createChatSearchIndexConfig()
        );

        // Index all messages
        chatHistory.forEach((message, index) => {
            if (message.content) {
                const searchable = getSearchableContent(message);
                const processedContent = preprocessContent(searchable);
                
                flexSearchIndex.current?.add({
                    id: `${SEARCH_ID_PREFIX.MESSAGE}${index}`,
                    messageIndex: index,
                    content: processedContent,
                });
            }
        });

        lastIndexedHistoryLengthRef.current = chatHistory.length;
        wasGeneratingRef.current = false;

        
    }, [chatHistory, isGenerating]);

    /**
     * Find all matches of search query using FlexSearch for fast indexed search
     */
    const findMatches = useCallback((query: string, roleFilterValue: RoleFilter = 'all'): SearchMatch[] => {
        if (!query || query.trim().length === 0) {
            return [];
        }
        
        const normalizedQuery = query.toLowerCase().trim();
        const foundMatches: SearchMatch[] = [];

        // Use FlexSearch to find relevant messages (fast indexed search)
        const searchResults = flexSearchIndex.current?.search(normalizedQuery, {
            index: 'content',
            limit: 1000,
            // Enable fuzzy suggestions only if:
            // 1. the query has 3 or more characters and
            // 2. the query contains at least one non-word (space/punctuation) character
            suggest: normalizedQuery.length >= CHAT_SEARCH_CONFIG.SUGGEST_MIN_LENGTH && /[\W_]/.test(normalizedQuery),
            
        });
        
        if (!searchResults || searchResults.length === 0) {
            return [];
        }

        // Extract message indices from FlexSearch results
        // FlexSearch returns an array of result objects
        const messageIndices = new Set<number>();
        
        if(Array.isArray(searchResults)) {
            searchResults.forEach((result: any) => {
                if (result && Array.isArray(result.result)) {
                    result.result.forEach((docId: string) => {
                        // Extract messageIndex from document ID (format: "msg_0", "msg_1", etc.)
                        const match = docId.match(/msg_(\d+)/);
                        if (match) {
                            messageIndices.add(parseInt(match[1], 10));
                        }
                    });
                }
            });
        }

        // For each matched message, find exact positions of the query string
        messageIndices.forEach((messageIndex) => {
            const message = chatHistory[messageIndex];
            if (!message?.content) return;

            // Apply role filter
            if (roleFilterValue !== 'all' && message.role !== roleFilterValue) {
                return;
            }

            // Use searchable content (excludes analysis for assistant messages)
            // to stay aligned with HighlightedText which only receives final response text
            const searchable = getSearchableContent(message);
            const content = searchable.toLowerCase();
            
            let matchIndex = 0;

            // Try to find exact matches of the query first
            let startIndex = 0;
            while (startIndex < content.length) {
                const index = content.indexOf(normalizedQuery, startIndex);
                if (index === -1) break;
               
                foundMatches.push({
                    messageIndex,
                    matchIndex,
                    startOffset: index,
                    endOffset: index + normalizedQuery.length,
                });

                matchIndex += 1;
                startIndex = index + 1;
            }
        });

        // Sort matches by message index and offset for proper navigation
        foundMatches.sort((a, b) => {
            if (a.messageIndex !== b.messageIndex) {
                return a.messageIndex - b.messageIndex;
            }
            return a.startOffset - b.startOffset;
        });


        return foundMatches;
    }, [chatHistory]);

    /**
     * Scroll to and highlight the current match
     */
    const scrollToMatch = useCallback((matchIndex: number) => {
        if (matches.length === 0 || matchIndex < 0 || matchIndex >= matches.length) {
            return;
        }

        const match = matches[matchIndex];
        const messageElement = chatContainerRef.current?.querySelector(
            `[data-message-index="${match.messageIndex}"]`
        );

        if (messageElement) {

            setTimeout(() => {
                const currentMatchElement = messageElement.querySelector(
                    '.search-match-current'
                );
                
                if (currentMatchElement) {
                    currentMatchElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                } else {
                    // Fallback to message element if highlight not found yet
                    messageElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }, 100);

            // Add visual highlight to the message
            messageElement.classList.add('search-highlight');
            setTimeout(() => {
                messageElement.classList.remove('search-highlight');
            }, 2000);
        }
    }, [matches, chatContainerRef]);

    /**
     * Handle search query changes with debouncing
     */
    const handleSearch = useCallback((query: string, targetIndex: number = 0) => {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        setSearchQuery(query);

        if (!query || query.trim().length === 0) {
            setMatches([]);
            setCurrentMatchIndex(-1);
            return;
        }

        // Debounce search for performance
        searchTimeoutRef.current = setTimeout(() => {
            const newMatches = findMatches(query, latestRoleFilterRef.current);
            setMatches(newMatches);
            
            if (newMatches.length > 0) {
                const validIndex = Math.min(targetIndex, newMatches.length - 1);
                setCurrentMatchIndex(validIndex);
            } else {
                setCurrentMatchIndex(-1);
            }
        }, CHAT_SEARCH_CONFIG.DEBOUNCE_MS);
    }, [findMatches]);

    /**
     * Navigate to specific match index
     */
    const navigateToMatch = useCallback((index: number) => {
        if (matches.length === 0) return;

        const validIndex = Math.max(0, Math.min(index, matches.length - 1));
        setCurrentMatchIndex(validIndex);
    }, [matches]);

    /**
     * Handle role filter changes
     */
    const handleRoleFilterChange = useCallback((newRoleFilter: RoleFilter) => {
        setRoleFilter(newRoleFilter);
        
        // Re-run search with new role filter
        if (latestQueryRef.current && latestQueryRef.current.trim().length > 0) {
            const newMatches = findMatches(latestQueryRef.current, newRoleFilter);
            setMatches(newMatches);
            
            if (newMatches.length > 0) {
                setCurrentMatchIndex(0);
            } else {
                setCurrentMatchIndex(-1);
            }
        }
    }, [findMatches]);

    /**
     * Clear search state
     */
    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setRoleFilter('all');
        setMatches([]);
        setCurrentMatchIndex(-1);
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
    }, []);

    /**
     * Get highlight data for rendering
     * Returns detailed info about which match is current
     */
    const getHighlightData = useCallback((messageIndex: number) => {
        if (!searchQuery || matches.length === 0) {
            return null;
        }

        const messageMatches = matches.filter(m => m.messageIndex === messageIndex);
        if (messageMatches.length === 0) {
            return null;
        }
        const currentMatch = matches[currentMatchIndex];
        const isCurrentMessage = currentMatch?.messageIndex === messageIndex;

        // Find which match within this message is the current one
        let currentMatchIndexInThisMessage = -1;
        if (isCurrentMessage && currentMatch) {
            // Use matchIndex directly since it's already the index within the message
            currentMatchIndexInThisMessage = currentMatch.matchIndex;
            
            // Verify this is correct
            const verifyMatch = messageMatches[currentMatch.matchIndex];
            if (verifyMatch && (verifyMatch.startOffset !== currentMatch.startOffset || 
                               verifyMatch.endOffset !== currentMatch.endOffset)) {
                console.warn(`[getHighlightData] Mismatch detected!`, {
                    expected: currentMatch,
                    found: verifyMatch,
                    messageMatches
                });
                // Fall back to finding by offset
                currentMatchIndexInThisMessage = messageMatches.findIndex(
                    m => m.startOffset === currentMatch.startOffset && 
                         m.endOffset === currentMatch.endOffset
                );
            }
        }

        /**
         * Uncomment to debug logging for found matches
         */
        // if (messageMatches.length > 0) {
        //     const message = chatHistory[messageIndex];
            
        //     messageMatches.forEach((match, idx) => {
        //         const matchedText = message.content.substring(match.startOffset, match.endOffset);
        //         const contextStart = Math.max(0, match.startOffset - 20);
        //         const contextEnd = Math.min(message.content.length, match.endOffset + 20);
        //         const contextText = message.content.substring(contextStart, contextEnd);
        //         const isCurrent = isCurrentMessage && idx === currentMatchIndexInThisMessage;
                
        //         console.log(`[useChatSearch] Match ${idx + 1}/${messageMatches.length} ${isCurrent ? '-> CURRENT' : ''}:`, {
        //             text: matchedText,
        //             context: `...${contextText}...`,
        //             position: `[${match.startOffset}-${match.endOffset}]`,
        //             matchIndex: match.matchIndex
        //         });
        //     });
        // }

        return {
            query: searchQuery,
            matches: messageMatches,
            isCurrentMatch: isCurrentMessage,
            currentMatchInMessage: currentMatchIndexInThisMessage,
            currentGlobalMatchIndex: currentMatchIndexInThisMessage,
        };
    }, [searchQuery, matches, currentMatchIndex]);

    useEffect(() => {
        if (matches.length === 0 || currentMatchIndex < 0) {
            return;
        }
        scrollToMatch(currentMatchIndex);
    }, [matches, currentMatchIndex, scrollToMatch]);

    useEffect(() => {
        const activeQuery = latestQueryRef.current;
        if (!activeQuery || activeQuery.trim().length === 0) {
            return;
        }

        const updatedMatches = findMatches(activeQuery, latestRoleFilterRef.current);
        setMatches(updatedMatches);
        setCurrentMatchIndex((prev) => {
            if (updatedMatches.length === 0) {
                return -1;
            }
            if (prev === -1) {
                return 0;
            }
            return Math.min(prev, updatedMatches.length - 1);
        });
    }, [chatHistory, findMatches]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    return {
        searchQuery,
        roleFilter,
        matches,
        currentMatchIndex,
        totalMatches: matches.length,
        handleSearch,
        handleRoleFilterChange,
        navigateToMatch,
        clearSearch,
        getHighlightData,
    };
};
